import { db } from '../firebase/firebase';
import { collection, getDocs, getDoc, doc, updateDoc, setDoc, deleteDoc, deleteField, where, query, onSnapshot, arrayUnion } from 'firebase/firestore';
import { PinManager } from './PinManager';
import { IProject, IPin } from '../types';
import { OrganisationManager } from './OrganisationManager';
import { isDemoModeWithTutorial, withDemoModeFallback, skipInDemoMode, getDemoOrganisationIfActive } from './base/demoModeUtils';
import { DemoDataManager } from '../services/demoDataManager';
import { isDemoMode } from '../utilities/demoMode';
import {
  getAvailabilityFieldsForStatus,
  type ProjectAvailabilityStatus,
} from '../utilities/projectAvailability';
import {
  projectFromDoc,
  isQRCodePinEnabled,
  filterPinsByProject,
  calculateUpdatedAudioSize,
  getSubjectTags,
  addSubjectTag,
  removeSubjectTag,
  buildCreateProjectPayload,
  buildSettingsFieldUpdates,
  resolveProjectConfig,
  getModeratorAndAdminUserKeys,
} from './project/projectNormalisers';
import {
  applyEpisodeModeTransitionIfNeeded,
  runEpisodeProjectMigration,
} from './project/episodeProjectMigrationRunner';
import type { EpisodeMigrationResult } from '../utils/episodeProjectMigration';
import { resetProjectDownloadCounts } from './project/resetDownloadCounts';
import { deleteProjectAndAssociatedData } from './project/projectDeletion';
import {
  addUserToProject as addUserToProjectAccess,
  removeUserFromProject as removeUserFromProjectAccess,
  updateUserProjectAccess as updateUserProjectAccessLevel,
  getProjectAccessUsers as fetchProjectAccessUsers,
  listenForProjectAccessChanges as subscribeProjectAccessChanges,
  addGroupToProject as addGroupToProjectAccess,
} from './project/access';
import {
  createInviteLink as createProjectInviteLink,
  validateInviteLink as validateProjectInviteLink,
} from './project/invites';
import {
  syncGroupMembershipChanges as syncProjectGroupMembershipChanges,
  syncProjectGroupChanges as applyProjectGroupChanges,
  getUserGroups as fetchUserGroups,
} from './project/groupSync';

export const ProjectManager = {
  observeProjects: async (): Promise<IProject[]> => {
    try {
      const querySnapshot = await getDocs(collection(db, 'Projects'));
      const projects: IProject[] = [];
      querySnapshot.forEach((docSnapshot) => {
        // Removed the isAvailable check to include all projects
        projects.push(projectFromDoc(docSnapshot.id, docSnapshot.data()));
      });
      return projects;
    } catch (error) {
      console.error("Error getting projects:", error);
      return [];
    }
  },

  getProject: async (project_id: string): Promise<IProject | null> => {
    try {
      if (!project_id) {
        throw new Error('Invalid project_id');
      }

      // In demo mode, still allow reading from Firestore for project data (like icons)
      // Only use demo data if in organisational-projects-tutorial
      if (isDemoMode()) {
        if (isDemoModeWithTutorial('organisational-projects-tutorial')) {
          const demoProjects = DemoDataManager.getDemoOrganisationProjects();
          const demoProject = demoProjects.find(p => p.project_id === project_id);
          if (demoProject) {
            return demoProject;
          }
        }
        // In demo mode but not in organisational tutorial, still read from Firestore
        // This allows icons and other project data to load in demo mode
        try {
          const projectDocRef = doc(db, 'Projects', project_id);
          const projectDoc = await getDoc(projectDocRef);
          if (!projectDoc.exists()) {
            return null;
          }
          return projectFromDoc(projectDoc.id, projectDoc.data());
        } catch (error) {
          console.warn('Error fetching project from Firestore in demo mode:', error);
          return null;
        }
      }

      // Normal mode: read from Firestore
      const projectDocRef = doc(db, 'Projects', project_id);
      const projectDoc = await getDoc(projectDocRef);
      if (!projectDoc.exists()) {
        return null;
      }
      return projectFromDoc(projectDoc.id, projectDoc.data());
    } catch (error) {
      console.error("Error fetching project:", error);
      return null;
    }
  },

  /**
   * Observe a single project's document for real-time updates.
   * Returns an unsubscribe function to stop listening.
   */
  observeProject: (project_id: string, callback: (project: IProject | null) => void): (() => void) => {
    if (!project_id) {
      console.error('ProjectManager.observeProject called with invalid project_id');
      callback(null);
      return () => {};
    }

    try {
      // In organisational demo tutorial mode, use demo data and do not attach a Firestore listener
      if (isDemoMode() && isDemoModeWithTutorial('organisational-projects-tutorial')) {
        const demoProjects = DemoDataManager.getDemoOrganisationProjects();
        const demoProject = demoProjects.find(p => p.project_id === project_id) || null;
        callback(demoProject);
        return () => {};
      }

      const projectDocRef = doc(db, 'Projects', project_id);
      const unsubscribe = onSnapshot(
        projectDocRef,
        (projectDoc) => {
          if (!projectDoc.exists()) {
            callback(null);
            return;
          }
          callback(projectFromDoc(projectDoc.id, projectDoc.data()));
        },
        (error) => {
          console.error('ProjectManager.observeProject: Error in listener:', error);
        }
      );

      return unsubscribe;
    } catch (error) {
      console.error('ProjectManager.observeProject: Failed to attach listener:', error);
      return () => {};
    }
  },

  getProjectIcon: async (project_id: string): Promise<string | null> => {
    try {
      const project = await ProjectManager.getProject(project_id);
      if (project && project.icon) {
        return project.icon;
      }
      return null;
    } catch (error) {
      console.error("Error fetching project icon:", error);
      return null;
    }
  },

  checkPinEnableForQRCodeOrNot: async (pin: any): Promise<boolean> => {
    try {
      const project = await ProjectManager.getProject(pin.project);
      return isQRCodePinEnabled(project, pin);
    } catch (error) {
      console.error("Error checking QR code enablement:", error);
      return false;
    }
  },

  getProjectPins: async (project_id: string): Promise<IPin[]> => {
    try {
      // Fetch all available pins
      const allPins = await PinManager.observePins();

      // Filter pins that belong to the given project
      return filterPinsByProject(allPins, project_id);
    } catch (error) {
      console.error(`Error fetching pins for project ${project_id}:`, error);
      return [];
    }
  },

  updateProjectAudioSize: async (project_id: string, additionalSize: number): Promise<void> => {
    try {
      const projectDocRef = doc(db, 'Projects', project_id);
      const projectDoc = await getDoc(projectDocRef);
  
      if (!projectDoc.exists()) {
        
        return;
      }
  
      const projectData = projectDoc.data() as IProject;
      const newAudioSize = calculateUpdatedAudioSize(projectData.audioSize, additionalSize);
  
      // Update the project's audioSize with the new total
      await updateDoc(projectDocRef, {
        audioSize: newAudioSize
      });
  
    } catch (error) {
      console.error("Error updating project audio size:", error);
    }
  },

  getSubjectTagsForProject: async (project_id: string): Promise<string[]> => {
    try {
      const projectDocRef = doc(db, 'Projects', project_id);
      const projectDoc = await getDoc(projectDocRef);
      if (projectDoc.exists()) {
        const projectData = projectDoc.data() as IProject;
        return getSubjectTags(projectData);
      }
      return [];
    } catch (error) {
      console.error("Error fetching subject tags:", error);
      return [];
    }
  },

  addSubjectTagToProject: async (project_id: string, tagName: string): Promise<void> => {
    try {
      const projectDocRef = doc(db, 'Projects', project_id);
      const projectDoc = await getDoc(projectDocRef);
      if (!projectDoc.exists()) {
        
        return;
      }
      const projectData = projectDoc.data() as IProject;
      await updateDoc(projectDocRef, {
        'tags.subjectTag': addSubjectTag(projectData.tags?.subjectTag, tagName)
      });
    } catch (error) {
      console.error("Error adding subject tag to project:", error);
    }
  },
  
  // Remove a tag from the project's subjectTags array
  removeSubjectTagFromProject: async (project_id: string, tagName: string): Promise<void> => {
    try {
      const projectDocRef = doc(db, 'Projects', project_id);
      const projectDoc = await getDoc(projectDocRef);
      if (!projectDoc.exists()) {
        
        return;
      }
      const projectData = projectDoc.data() as IProject;
      const updatedTags = removeSubjectTag(projectData.tags?.subjectTag, tagName);
      if (updatedTags !== null) {
        await updateDoc(projectDocRef, {
          'tags.subjectTag': updatedTags
        });
        
      }
    } catch (error) {
      console.error("Error removing subject tag from project:", error);
    }
  },

  updateContentTagForProject: async (project_id: string, contentTag: string): Promise<void> => {
    try {
      const projectDocRef = doc(db, 'Projects', project_id);
      const projectDoc = await getDoc(projectDocRef);
  
      if (!projectDoc.exists()) {
        
        return;
      }
  
      // Update the project's contentTag
      await updateDoc(projectDocRef, {
        'tags.contentTag': contentTag
      });
  
    } catch (error) {
      console.error("Error updating content tag for project:", error);
    }
  },

  createProject: async (project_id: string, projectData: IProject): Promise<void> => {
    try {
      // Create a reference to the new project document with the specified project_id
      const projectDocRef = doc(collection(db, 'Projects'), project_id);

      // Set the new project document data with the project_id as its document ID
      await setDoc(projectDocRef, buildCreateProjectPayload(projectData));

    } catch (error) {
      console.error("Error creating new project with key " + project_id + ":", error);
    }
  },
  updateProject: async (project_id: string, updatedProjectData: Partial<IProject>): Promise<IProject | null> => {
    try {
      const projectDocRef = doc(db, 'Projects', project_id);
  
      await updateDoc(projectDocRef, {
        ...updatedProjectData,
        // Only update fields that are present in updatedProjectData
      });
  
      // Fetch and return the updated project data
      return await ProjectManager.getProject(project_id);
    } catch (error) {
      console.error(`Error updating project with key ${project_id}:`, error);
      return null;
    }
  },

  updateProjectSettingsFields: async (
    project_id: string,
    settingsFields: Record<string, unknown>
  ): Promise<IProject | null> => {
    try {
      const projectDocRef = doc(db, 'Projects', project_id);
      const updateData = buildSettingsFieldUpdates(settingsFields);

      await updateDoc(projectDocRef, updateData as any);
      return await ProjectManager.getProject(project_id);
    } catch (error) {
      console.error(`Error updating project settings for project ${project_id}:`, error);
      return null;
    }
  },

  clearProjectSetting: async (project_id: string, settingKey: string): Promise<IProject | null> => {
    try {
      const projectDocRef = doc(db, 'Projects', project_id);
      await updateDoc(projectDocRef, {
        [`settings.${settingKey}`]: deleteField()
      });
      return await ProjectManager.getProject(project_id);
    } catch (error) {
      console.error(`Error clearing project setting ${settingKey} for project ${project_id}:`, error);
      return null;
    }
  },
  deleteProject: async (project_id: string): Promise<void> => {
    try {
      const projectDocRef = doc(db, 'Projects', project_id);
      await deleteDoc(projectDocRef);
    } catch (error) {
      console.error(`Error deleting project with key ${project_id}:`, error);
    }
  },

  deleteProjectAndAssociatedData: async (project_id: string): Promise<void> => {
    return deleteProjectAndAssociatedData(project_id);
  },


  updateProjectAvailability: async (project_id: string, isAvailable: boolean): Promise<void> => {
    const status: ProjectAvailabilityStatus = isAvailable ? 'live' : 'off';
    await ProjectManager.updateProjectAvailabilityStatus(project_id, status);
  },

  updateProjectAvailabilityStatus: async (
    project_id: string,
    status: ProjectAvailabilityStatus
  ): Promise<void> => {
    try {
      const projectDocRef = doc(db, 'Projects', project_id);
      await updateDoc(projectDocRef, getAvailabilityFieldsForStatus(status));
    } catch (error) {
      console.error(`Error updating project availability for project ${project_id}:`, error);
    }
  },
  getProjectConfig: async (project_id: string): Promise<any> => {
    const projectDocRef = doc(db, 'Projects', project_id);
    const projectDoc = await getDoc(projectDocRef);
    if (projectDoc.exists()) {
      const projectData = projectDoc.data();
      return resolveProjectConfig(projectData.config); // Include default password
    }
    return null;
  },
  
  updateProjectConfig: async (project_id: string, config: any): Promise<void> => {
    const projectDocRef = doc(db, 'Projects', project_id);
    await updateDoc(projectDocRef, { config });
  },

  setEpisodeAlbumKey: async (project_id: string, albumKey: string): Promise<void> => {
    try {
      const projectDocRef = doc(db, 'Projects', project_id);
      await updateDoc(projectDocRef, { episodeAlbumKey: albumKey });
    } catch (error) {
      console.error("Error setting episode album key:", error);
    }
  },

  getEpisodeAlbumKey: async (project_id: string): Promise<string | null> => {
    try {
      const project = await ProjectManager.getProject(project_id);
      return project?.episodeAlbumKey || null;
    } catch (error) {
      console.error("Error getting episode album key:", error);
      return null;
    }
  },

  /**
   * When a project is switched to episode mode, consolidate pins and recordings
   * onto a shared episode album and set PinModel for the mobile app.
   */
  migrateProjectToEpisodeMode: async (project_id: string): Promise<EpisodeMigrationResult> => {
    return runEpisodeProjectMigration(project_id);
  },

  applyEpisodeModeTransitionIfNeeded: async (
    project_id: string,
    previousEnabled: boolean | undefined,
    nextEnabled: boolean | undefined
  ): Promise<EpisodeMigrationResult | null> => {
    return applyEpisodeModeTransitionIfNeeded(project_id, previousEnabled, nextEnabled);
  },

  setProjectPrivacy: async (project_id: string, isPrivate: boolean): Promise<void> => {
    try {
      const projectDocRef = doc(db, 'Projects', project_id);
      await updateDoc(projectDocRef, { isPrivate });
    } catch (error) {
      console.error(`Error setting privacy for project ${project_id}:`, error);
    }
  },

  getProjectPrivacy: async (project_id: string): Promise<boolean | null> => {
    try {
      const project = await ProjectManager.getProject(project_id);
      return project?.isPrivate ?? null;
    } catch (error) {
      console.error(`Error getting privacy for project ${project_id}:`, error);
      return null;
    }
  },

  createInviteLink: async (project_id: string, accessLevel: 'read' | 'write' | 'admin' = 'read'): Promise<string | null> => {
    return createProjectInviteLink(project_id, accessLevel);
  },

  validateInviteLink: async (inviteId: string): Promise<{ project_id: string, accessLevel: string } | null> => {
    return validateProjectInviteLink(inviteId);
  },

  addUserToProject: async (userId: string, project_id: string, accessLevel: 'read' | 'write' | 'admin' | 'record'): Promise<void> => {
    return addUserToProjectAccess(userId, project_id, accessLevel);
  },

  listenForProjectAccessChanges: (userId: string, callback: () => void): (() => void) => {
    return subscribeProjectAccessChanges(userId, callback);
  },

  addPinToProject: async (project_id: string, pinKey: string): Promise<void> => {
    // Skip Firebase in demo mode - pins are stored locally
    await skipInDemoMode(async () => {
      try {
        // arrayUnion is concurrent-safe and idempotent (no read-modify-write race)
        await updateDoc(doc(db, 'Projects', project_id), {
          pins: arrayUnion(pinKey),
        });
      } catch (error) {
        console.error(`Error adding pin ${pinKey} to project ${project_id}:`, error);
      }
    });
  },

  addGroupToProject: async (project_id: string, groupId: string, accessLevel: 'read' | 'record'): Promise<void> => {
    return addGroupToProjectAccess(project_id, groupId, accessLevel);
  },

  observeModeratorProjects: async (moderatorId: string): Promise<IProject[]> => {
    const getDemoProjects = () => {
      if (isDemoModeWithTutorial('organisational-projects-tutorial')) {
        const demoOrg = DemoDataManager.getDemoOrganisation();
        if (demoOrg) {
          const demoProjects = DemoDataManager.getDemoOrganisationProjects();
          return demoProjects.filter(p => 
            p.projectOwnerUserKey === 'demo-moderator-001' || 
            p.projectOwnerUserKey === 'demo-moderator-002'
          );
        }
      }
      return [];
    };

    return await withDemoModeFallback(
      getDemoProjects,
      async () => {
        const projectsRef = collection(db, 'Projects');
        const q = query(projectsRef, where('projectOwnerUserKey', '==', moderatorId));
        const querySnapshot = await getDocs(q);
        const projects: IProject[] = [];
        querySnapshot.forEach((docSnapshot) => {
          projects.push(projectFromDoc(docSnapshot.id, docSnapshot.data()));
        });
        return projects;
      },
      getDemoProjects
    ).catch(() => []);
  },

  observeOrganisationProjects: async (organisationId: string): Promise<IProject[]> => {
    return await withDemoModeFallback(
      async () => {
        if (isDemoModeWithTutorial('organisational-projects-tutorial')) {
          const demoOrg = getDemoOrganisationIfActive();
          if (demoOrg && demoOrg.id === organisationId) {
            return DemoDataManager.getDemoOrganisationProjects();
          }
        }
        return [];
      },
      async () => {
        try {
          // First get all moderators and admins in the organisation
          const members = await OrganisationManager.getOrganisationMembers(organisationId);
          const moderatorIds = getModeratorAndAdminUserKeys(members);

          if (moderatorIds.length === 0) {
            return [];
          }

          // Then get all projects where projectOwnerUserKey is in the list of moderator IDs
          const projectsRef = collection(db, 'Projects');
          const q = query(projectsRef, where('projectOwnerUserKey', 'in', moderatorIds));
          const querySnapshot = await getDocs(q);
          const projects: IProject[] = [];
          querySnapshot.forEach((docSnapshot) => {
            projects.push(projectFromDoc(docSnapshot.id, docSnapshot.data()));
          });
          return projects;
        } catch (error) {
          console.error("Error getting organisation projects:", error);
          return [];
        }
      },
      async () => {
        if (isDemoModeWithTutorial('organisational-projects-tutorial')) {
          const demoOrg = getDemoOrganisationIfActive();
          if (demoOrg && demoOrg.id === organisationId) {
            return DemoDataManager.getDemoOrganisationProjects();
          }
        }
        return [];
      }
    );
  },

  syncGroupMembershipChanges: async (
    groupId: string,
    addedUserIds: string[],
    removedUserIds: string[]
  ): Promise<void> => {
    return syncProjectGroupMembershipChanges(groupId, addedUserIds, removedUserIds);
  },

  getUserGroups: async (userId: string): Promise<string[]> => {
    return fetchUserGroups(userId);
  },

  removeUserFromProject: async (userId: string, project_id: string): Promise<void> => {
    return removeUserFromProjectAccess(userId, project_id);
  },

  getProjectAccessUsers: async (project_id: string): Promise<any[]> => {
    return fetchProjectAccessUsers(project_id);
  },

  updateUserProjectAccess: async (userId: string, project_id: string, newAccessLevel: 'read' | 'write' | 'admin' | 'record'): Promise<void> => {
    return updateUserProjectAccessLevel(userId, project_id, newAccessLevel);
  },

  syncProjectGroupChanges: async (
    project_id: string,
    oldGroupIds: string[],
    newGroupIds: string[]
  ): Promise<void> => {
    return applyProjectGroupChanges(project_id, oldGroupIds, newGroupIds);
  },

  resetProjectDownloadCounts: async (project_id: string): Promise<number> => {
    return resetProjectDownloadCounts(project_id);
  },
}